/**
 * @fileoverview LS Favourites provider — ls-favourites microservice backend
 * @module FavouritesLSProvider
 */

(function() {
  'use strict';

  const GUEST_STORAGE_KEY = 'ls_fav_guest_id';
  const GUEST_COOKIE = 'ls_fav_guest';
  const ACCOUNT_COOKIE = 'ls_account';
  const GUEST_HEADER = 'X-LS-Fav-Guest';

  const config = window.LS_FAVOURITES_CONFIG || { backend: 'ls-favourites', currency: 'GBP' };
  const changeListeners = [];
  let cacheItems = [];
  let readyResolve;
  let readyResolved = false;
  let initialised = false;

  const ready = new Promise((resolve) => {
    readyResolve = resolve;
    setTimeout(() => {
      if (!readyResolved) {
        console.warn('[FavouritesLS] Ready timeout — API may be unreachable.');
        resolveReady();
      }
    }, 15000);
  });

  function resolveReady() {
    if (readyResolved) return;
    readyResolved = true;
    readyResolve();
  }

  function notifyChange() {
    changeListeners.forEach((fn) => fn());
  }

  function normalizeProductId(value) {
    const id = String(value);
    return id.includes('/') ? id.split('/').pop() : id;
  }

  function normalizeSku(value) {
    return String(value || '').trim().toUpperCase();
  }

  function getApiBaseUrl() {
    const url = config.apiUrl;
    if (!url) {
      console.error('[FavouritesLS] LS_FAVOURITES_CONFIG.apiUrl is not set.');
      return null;
    }
    return String(url).replace(/\/$/, '');
  }

  function readCookie(name) {
    const match = document.cookie.match(new RegExp('(?:^|;\\s*)' + name + '=([^;]*)'));
    return match ? decodeURIComponent(match[1]) : null;
  }

  function getStoredGuestId() {
    try {
      return localStorage.getItem(GUEST_STORAGE_KEY) || null;
    } catch (error) {
      return null;
    }
  }

  function setStoredGuestId(guestId) {
    if (!guestId) return;
    try {
      localStorage.setItem(GUEST_STORAGE_KEY, String(guestId).toLowerCase());
    } catch (error) {
      console.warn('[FavouritesLS] Could not persist guest id:', error);
    }
  }

  function clearStoredGuestId() {
    try {
      localStorage.removeItem(GUEST_STORAGE_KEY);
    } catch (error) {
      /* ignore */
    }
  }

  function getGuestIdForRequest() {
    const cookieGuest = readCookie(GUEST_COOKIE);
    if (cookieGuest) return cookieGuest;
    return getStoredGuestId();
  }

  function hasAccountCookie() {
    return Boolean(readCookie(ACCOUNT_COOKIE));
  }

  function persistGuestIdFromResponse(data) {
    if (data && data.guest_id) {
      setStoredGuestId(data.guest_id);
    }
  }

  function rebuildCache(results) {
    cacheItems = (Array.isArray(results) ? results : []).map((item) => ({
      product_id: normalizeProductId(item.product_id),
      product_sku: normalizeSku(item.product_sku)
    })).filter((item) => item.product_id && item.product_sku);
  }

  function findByProductId(productId) {
    const id = normalizeProductId(productId);
    return cacheItems.find((item) => item.product_id === id) || null;
  }

  function findBySku(sku) {
    const normalized = normalizeSku(sku);
    return cacheItems.find((item) => item.product_sku === normalized) || null;
  }

  function upsertCacheItem(item) {
    if (!item || !item.product_id || !item.product_sku) return;
    const productId = normalizeProductId(item.product_id);
    const productSku = normalizeSku(item.product_sku);
    const existingIndex = cacheItems.findIndex(
      (entry) => entry.product_id === productId || entry.product_sku === productSku
    );
    const next = { product_id: productId, product_sku: productSku };
    if (existingIndex >= 0) {
      cacheItems[existingIndex] = next;
      return;
    }
    cacheItems.push(next);
  }

  function removeCacheItem(productId, sku) {
    const id = productId != null ? normalizeProductId(productId) : null;
    const normalizedSku = sku != null ? normalizeSku(sku) : null;
    cacheItems = cacheItems.filter((item) => {
      if (normalizedSku && item.product_sku === normalizedSku) return false;
      if (id && item.product_id === id) return false;
      return true;
    });
  }

  function buildPayload(productId, context) {
    let sku = context?.sku || context?.productSku || null;
    let priceCents = context?.priceCents;
    const currency = (context?.currency || config.currency || 'GBP').toUpperCase();

    if (context?.type === 'pdp' && context.pdpData) {
      sku = sku || context.pdpData.sku;
      if (priceCents == null && context.pdpData.price != null) {
        priceCents = Math.round(Number(context.pdpData.price) * 100);
      }
    }

    if (!sku) {
      const cached = findByProductId(productId);
      sku = cached?.product_sku || null;
    }

    if (priceCents == null) {
      priceCents = 0;
    }

    return {
      product_id: normalizeProductId(productId),
      product_sku: normalizeSku(sku),
      price: Math.max(0, Math.round(Number(priceCents) || 0)),
      currency
    };
  }

  async function apiRequest(path, options = {}) {
    const baseUrl = getApiBaseUrl();
    if (!baseUrl) {
      throw new Error('[FavouritesLS] API URL not configured');
    }

    const headers = {
      'Content-Type': 'application/json',
      ...(options.headers || {})
    };

    const guestId = getGuestIdForRequest();
    if (guestId) {
      headers[GUEST_HEADER] = guestId;
    }

    const response = await fetch(baseUrl + path, {
      method: options.method || 'GET',
      credentials: 'include',
      headers,
      body: options.body != null ? JSON.stringify(options.body) : undefined
    });

    if (response.status === 204) {
      return null;
    }

    let data = null;
    const contentType = response.headers.get('content-type') || '';
    if (contentType.includes('application/json')) {
      data = await response.json();
    }

    if (!response.ok) {
      const detail = data?.errors?.[0]?.detail || response.statusText;
      throw new Error(detail || 'Request failed');
    }

    persistGuestIdFromResponse(data);
    return data;
  }

  async function fetchList() {
    const data = await apiRequest('/api/favourites/');
    rebuildCache(data?.results || []);
    return cacheItems;
  }

  async function tryMerge() {
    if (!hasAccountCookie()) return;
    const guestId = getGuestIdForRequest();
    if (!guestId) return;

    try {
      await apiRequest('/api/favourites/merge/', { method: 'POST' });
      try {
        await apiRequest('/api/saved-searches/merge/', { method: 'POST' });
      } catch (error) {
        console.warn('[FavouritesLS] Saved searches merge failed:', error);
      }
      clearStoredGuestId();
    } catch (error) {
      console.warn('[FavouritesLS] Merge failed:', error);
    }
  }

  async function initialise() {
    if (initialised) return;
    initialised = true;

    try {
      await tryMerge();
      const items = await fetchList();
      console.log('[FavouritesLS] Initialised —', items.length, 'favourite(s)');
    } catch (error) {
      console.error('[FavouritesLS] Init error:', error);
    } finally {
      resolveReady();
      notifyChange();
    }
  }

  window.FavouritesLSProvider = {
    ready,

    onChange(fn) {
      if (typeof fn === 'function') changeListeners.push(fn);
    },

    getAll() {
      return cacheItems.map((item) => item.product_id);
    },

    getAllSkus() {
      return cacheItems.map((item) => item.product_sku);
    },

    has(productId) {
      return Boolean(findByProductId(productId));
    },

    getItem(productId) {
      return findByProductId(productId);
    },

    count() {
      return cacheItems.length;
    },

    async add(productId, context) {
      if (!context?.skipDuplicateCheck && this.has(productId)) {
        return false;
      }

      const payload = buildPayload(productId, context);
      if (!payload.product_sku) {
        console.error('[FavouritesLS] Missing product_sku for add:', productId);
        return false;
      }

      try {
        const data = await apiRequest('/api/favourites/', {
          method: 'POST',
          body: payload
        });
        upsertCacheItem(data || payload);
        notifyChange();
        return true;
      } catch (error) {
        if (this.has(productId)) {
          return true;
        }
        console.error('[FavouritesLS] add error:', error);
        return false;
      }
    },

    async remove(productId, variantId, context) {
      const sku = context?.sku
        || context?.productSku
        || findByProductId(productId)?.product_sku
        || null;

      if (!sku) {
        console.error('[FavouritesLS] Missing product_sku for remove:', productId);
        return false;
      }

      try {
        await apiRequest('/api/favourites/' + encodeURIComponent(normalizeSku(sku)) + '/', {
          method: 'DELETE'
        });
        removeCacheItem(productId, sku);
        notifyChange();
        return true;
      } catch (error) {
        console.error('[FavouritesLS] remove error:', error);
        return false;
      }
    },

    async clear() {
      const items = [...cacheItems];
      for (const item of items) {
        try {
          await apiRequest('/api/favourites/' + encodeURIComponent(item.product_sku) + '/', {
            method: 'DELETE'
          });
        } catch (error) {
          console.error('[FavouritesLS] clear error:', error);
        }
      }
      cacheItems = [];
      notifyChange();
    },

    optimisticAdd(productId, context) {
      const payload = buildPayload(productId, context);
      if (!payload.product_sku) return;
      if (this.has(productId)) return;
      upsertCacheItem(payload);
      notifyChange();
    },

    optimisticRemove(productId) {
      const item = findByProductId(productId);
      if (!item) return;
      removeCacheItem(productId, item.product_sku);
      notifyChange();
    }
  };

  initialise();
})();
